import { Box, Flex, Table, Tag, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

// Custom components
import SortIndicator from '../../../components/table/SortIndicator';
import { useMemo, useState } from 'react';
import ErrorState from '../../../components/states/ErrorState';
import PageCard from '../../../components/page-card';
import { translate } from '../../../i18n';
import { UserMembershipDiscountType, UserMembershipDto } from '../../../models/user-membership/dto/user-membership.dto';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import { dateToString } from '../../../utils/date';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetTimeWindowGroups } from '../../time-windows/hooks/useGetTimeWindowGroups';
import { useGetUserMemberships } from '../hooks/useGetUserMemberships';
import AddUserMembershipModal from './AddUserMembershipModal';

const columnHelper = createColumnHelper<UserMembershipDto>();

const formatDiscountValue = (value: number, type: UserMembershipDiscountType) => {
    return type === UserMembershipDiscountType.PERCENTAGE ? `${value}%` : value;
};

export default function UserMembershipList({ userId }: { userId: string }) {
    const { selectedOrganization } = useSelectedOrganization();
    const { data, isLoading, error } = useGetUserMemberships(userId);
    /**
     * R92-4 — this list does not RENDER the groups, it uses them to NAME the
     * time-window discounts on each membership row
     * (`timeWindowGroups.find(...)` → `{group?.name}: 20%`). Post-R89-1 a
     * refused read leaves `data === undefined`, so every one of those labels
     * collapsed to `": 20%"` — a discount the operator cannot attribute to any
     * time window, on the page where a member's entitlements are checked.
     *
     * The table is NOT withheld: the memberships themselves come from a
     * different query with its own error state, and removing them because an
     * unrelated read failed is the defect one component over (run 92's
     * self-critique 3). The failure is stated above the table instead.
     */
    const { data: timeWindowGroups = [], isError: isTimeWindowGroupsError } =
        useGetTimeWindowGroups(selectedOrganization?.id);

    const [selectedMembership, setSelectedMembership] = useState<UserMembershipDto>();
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const columns = useMemo(() => [
        columnHelper.accessor('id', {
            id: 'id',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {'id'}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),

        columnHelper.accessor('startDate', {
            id: 'startDate',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('userMembership.startDate')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize='sm' fontWeight='700'>
                    {dateToString(new Date(info.getValue()))}
                </Text>
            )
        }),
        columnHelper.accessor('endDate', {
            id: 'endDate',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('userMembership.endDate')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize='sm' fontWeight='700'>
                    {dateToString(new Date(info.getValue()))}
                </Text>
            )
        }),
        columnHelper.accessor('isActive', {
            id: 'isActive',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('userMembership.status')}
                </Text>
            ),
            cell: (info) => (
                <Flex align='center' gap={2}>
                    <MembershipStatus membership={info.row.original} />
                    {/* MYS-117 Duo (F2): beneficiary rows are visually distinct —
                        this membership is derived from someone else's paying
                        subscription; controls live on the payer's fiche. */}
                    {info.row.original.isAttachedBeneficiary && (
                        <Tag colorScheme='blue' size='sm' variant='subtle'>
                            {translate('userMembership.duo_badge_beneficiary')}
                        </Tag>
                    )}
                </Flex>
            )
        }),

        columnHelper.accessor('type.timeWindowDiscounts', {
            id: 'timeWindowDiscounts',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('userMembership.time_window_discounts')}
                </Text>
            ),
            cell: (info) => {
                const discounts = info.getValue();
                if (!discounts?.length) return '-';
                return discounts.map(discount => {
                    const group = timeWindowGroups.find(g => g.id === discount.timeWindowGroupId);
                    return (
                        <Box key={discount.id} mb={1}>
                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                {group?.name}: {formatDiscountValue(discount.courtBookingDiscountValue, discount.courtBookingDiscountType)}
                            </Text>
                        </Box>
                    );
                });
            }
        }),
        columnHelper.accessor('type.coachingSessionDiscountValue', {
            id: 'coachingSessionDiscountValue',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('userMembership.coachingSessionDiscountValue')}
                </Text>
            ),
            cell: (info) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {formatDiscountValue(info.getValue(), info.row.original.type.coachingSessionDiscountType)}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('type.maxCourtBookingTimeInAdvance', {
            id: 'maxCourtBookingTimeInAdvance',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('userMembership.maxCourtBookingTimeInAdvance')}
                </Text>
            ),
            cell: (info) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue() / 1000 / 60 / 60 / 24}
                    </Text>
                </Flex>
            )
        }),
    ], [textColor, timeWindowGroups]);
    const sortedData = useMemo(
        () => [...(data || [])].sort((a, b) => -a.startDate.localeCompare(b.startDate)),
        [data],
    );

    const table = useReactTable({
        data: sortedData,
        columns,
        state: {
            sorting
        },
        initialState: {
            columnVisibility: {
                id: false,
            }
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    return (
        <PageCard title={translate('userMembership.memberships')} rightHeader={
            <AddUserMembershipModal
                userId={userId}
                selectedMembership={selectedMembership}
                setSelectedMembership={setSelectedMembership}
            />}
        >
            <Box>
                {isTimeWindowGroupsError && (
                    <Text color='danger.500' fontSize='sm' mb={2}>
                        {translate('time_windows.error_loading')}
                    </Text>
                )}
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            {...tableThProps}
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                {...tableThFlexProps}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr key={row.id}
                                    {...tableRowProps}
                                    onClick={() => {
                                        // MYS-117 Duo (F2): beneficiary rows are
                                        // subscription-managed — no edit modal (the
                                        // API would reject the write anyway).
                                        if (row.original.isAttachedBeneficiary) return;
                                        setSelectedMembership({ ...row.original });
                                    }}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                key={cell.id}
                                                {...tableCellProps}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
                {isLoading && !data && (
                    <Box textAlign="center" py={4}>
                        <Text color="gray.500">{translate('common.loading')}</Text>
                    </Box>
                )}
                {error && (
                    <ErrorState message={translate('common.error')} />
                )}
                {!isLoading && sortedData.length === 0 && (
                    <Box textAlign="center" py={4}>
                        <Text color="gray.500">{translate('userMembership.no_memberships')}</Text>
                    </Box>
                )}
            </Box>
        </PageCard>
    );
}

function MembershipStatus({ membership }: { membership: UserMembershipDto }) {
    const isActive = membership.isActive;
    const hasExpired = new Date(membership.endDate) < new Date();

    let colorScheme: string;
    let text: string;
    if (isActive && !hasExpired) {
        colorScheme = 'green';
        text = translate('userMembership.active');
    } else if (hasExpired) {
        colorScheme = 'yellow';
        text = translate('userMembership.expired');
    } else {
        colorScheme = 'red';
        text = translate('userMembership.inactive');
    }

    return (
        <Tag colorScheme={colorScheme} size='sm'>
            {text}
        </Tag>
    );
}